import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment-timezone";
import { ROUTE_PATH } from "utils/constants";
import { adminService } from "apiServices";
import { BGLoading, FormFields, PaginationNormal, TableCustom } from "components";
import renderTooltipCell from "utils/functions/renderTooltipCell";
import HeaderWithActions from "components/Displays/HeaderWithActions";
import EmptyState from "components/Displays/EmptyState";
import { AuditLogCompanyMissionV2ContainerStyled } from "./styled";
import { perPageOptions } from "utils/constants/options";

const AuditLogCompanyMissionV2Container = () => {
  const history = useHistory();
  const { control, watch } = useForm({
    defaultValues: {
      start_month: moment().startOf("month").toDate(),
      end_month: moment().endOf("month").toDate(),
    },
  });

  const watchStartMonth = watch("start_month");
  const watchEndMonth = watch("end_month");
  const [auditLogData, setAuditLogData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isBgLoading, setIsBgLoading] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [perPage, setPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const handleToMainPage = useCallback(() => {
    history.push(ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2);
  }, [history]);

  const handleDateChange = useCallback(() => {
    setIsFilter(true);
    setCurrentPage(1);
  }, []);

  const fetchData = useCallback(async () => {
    if (!watchStartMonth || !watchEndMonth) return;
    setIsBgLoading(true);
    const beginDate = moment(watchStartMonth).startOf("month").format("YYYY-MM-DD");
    const endDate = moment(watchEndMonth).endOf("month").format("YYYY-MM-DD");
    const offset = (currentPage - 1) * perPage;
    const stringQuery = `?begin_date=${beginDate}&end_date=${endDate}&per_page=${perPage}&offset=${offset}`;
    const res = await adminService.GET_MISSION_V2_AUDIT_LOGS(stringQuery);
    if (res?.success) {
      setAuditLogData(res?.data || []);
      setPagination(res?.page ? { ...res.page } : null);
    }
    setIsBgLoading(false);
  }, [watchStartMonth, watchEndMonth, currentPage, perPage]);

  const handleChangePerPage = useCallback((newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleClickPage = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const columns = useMemo(
    () => {
      const cellEllipsis = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };
      return [
        {
          title: "ลำดับ",
          field: "no",
          headerStyle: { textAlign: "left", maxWidth: "5%" },
          style: { textAlign: "left", maxWidth: "5%" },
        },
        {
          title: "Username",
          field: "actor_username",
          headerStyle: { textAlign: "left", maxWidth: "10%" },
          style: { textAlign: "left", maxWidth: "10%", ...cellEllipsis },
        },
        {
          title: "ชื่อ - นามสกุล",
          field: "actor_detail",
          headerStyle: { textAlign: "left", maxWidth: "15%" },
          style: { textAlign: "left", maxWidth: "15%", ...cellEllipsis },
        },
        {
          title: "วัน - เวลา",
          field: "created_at",
          headerStyle: { textAlign: "left", maxWidth: "15%" },
          style: { textAlign: "left", maxWidth: "15%" },
          render: ({ origin }) =>
            moment(origin.created_at).add(543, "years").format("DD/MM/YYYY HH:mm"),
        },
        {
          title: "รายละเอียดกิจกรรม",
          field: "event_summary",
          headerStyle: { textAlign: "left", maxWidth: "55%" },
          style: { textAlign: "left", maxWidth: "55%", ...cellEllipsis },
          render: ({ value, origin }) =>
            renderTooltipCell({
              tooltipId: `audit-activity-${origin.id}`,
              value: value ?? "-",
              valueTooltip: value ?? "-",
            }),
        },
      ];
    },
    []
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <AuditLogCompanyMissionV2ContainerStyled>
      <BGLoading visible={isBgLoading} />
      <>
        <div className="content_section">
          <HeaderWithActions
            theme_admin
            handleBackCustom={handleToMainPage}
            title="รายการบันทึกจัดการภารกิจองค์กร"
            subTitle="Audit log"
          />
          <div className="date_range_row">
            <div className="date_range_field">
              <FormFields.FormDatePickerNew
                theme_standard
                control={control}
                name="start_month"
                format="MMMM YYYY"
                buddhistYear
                showMonthYearPicker
                maxDate={watchEndMonth}
                onChangeCustom={handleDateChange}
              />
            </div>
            <div className="between_label">ถึง</div>
            <div className="date_range_field">
              <FormFields.FormDatePickerNew
                theme_standard
                control={control}
                name="end_month"
                format="MMMM YYYY"
                minDate={watchStartMonth}
                maxDate={moment().endOf("month").toDate()}
                buddhistYear
                showMonthYearPicker
                onChangeCustom={handleDateChange}
              />
            </div>
          </div>

          <div className="table_section">
            <TableCustom.TableNormal
              theme_audit_log
              columns={columns}
              data={auditLogData}
              showPagination={false}
              pagination={pagination}
              useNo
              useRenderNoPage
              labelEmpty={
                <EmptyState
                  title={
                    isFilter
                      ? "ไม่พบข้อมูลรายการบันทึกจัดการภารกิจองค์กรที่คุณต้องการ"
                      : "ไม่มีรายการบันทึกจัดการภารกิจองค์กร"
                  }
                  subTitle={
                    isFilter
                      ? "กรุณาตรวจสอบเงื่อนไขการค้นหาอีกครั้ง"
                      : undefined
                  }
                />
              }
              isUseResetSelect={false}
            />
          </div>
        </div>
        <div className="pagination_section">
          <PaginationNormal
            perPageOptions={perPageOptions}
            currentPage={pagination?.current_page || 1}
            fromOrder={pagination?.from || 0}
            toOrder={pagination?.to || 0}
            perPage={perPage}
            totalPage={pagination?.total || 0}
            lastPage={pagination?.last_page || 1}
            onChangePerPage={handleChangePerPage}
            onClickPage={handleClickPage}
            widthbox="72px"
            alwaysShowArrows
          />
        </div>
      </>
    </AuditLogCompanyMissionV2ContainerStyled>
  );
};

export default AuditLogCompanyMissionV2Container;
